import axios from "axios";
import { getHiveflowConfig } from "@/lib/environment";
import { cacheOnlineCollection, installOfflineTransport, recoverWithOfflineTransport } from "@/lib/offlineTransport";
import { guestModeStored } from "@/lib/guestMode";

const BACKEND_URL = getHiveflowConfig().publicApiUrl;
export const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

export const api = axios.create({ baseURL: API, timeout: 30000 });

/**
 * Marker type so consumers (and the global unhandledrejection handler
 * in index.js) can distinguish a real offline rejection from another
 * application/network failure. Real errors must remain distinguishable.
 */
export class OfflineError extends Error {
  constructor(message = "offline", original = null) {
    super(message);
    this.name = "OfflineError";
    this.isOffline = true;
    this.original = original;
  }
}

function clientReportsOffline() {
  const browserOffline = typeof navigator !== "undefined" && navigator.onLine === false;
  const bridge = typeof window !== "undefined" ? window.__hiveflowOfflineRepository : null;
  const nativeOffline = Boolean(bridge?.nativeApp && bridge.online === false);
  return browserOffline || nativeOffline;
}

export function isOfflineError(err) {
  if (!err) return false;
  if (err.isOffline) return true;

  const msg = err.message || "";
  const networkFailure = !err.response && (
    msg === "Network Error"
    || msg === "Network request failed"
    || err.code === "ERR_NETWORK"
  );
  return networkFailure && clientReportsOffline();
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("imker_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return installOfflineTransport(config, guestModeStored());
});

api.interceptors.response.use(
  (res) => {
    if (res.status >= 200 && res.status < 300) cacheOnlineCollection(res.config, res.data);
    return res;
  },
  (err) => {
    if (err?.isOfflineServerAction || err?.code === "OFFLINE_SERVER_ACTION") return Promise.reject(err);
    if (isOfflineError(err)) {
      return recoverWithOfflineTransport(err).catch(() => Promise.reject(new OfflineError("Offline — verzoek niet verzonden", err)));
    }
    if (!err.response || err.response.status >= 500) return recoverWithOfflineTransport(err);
    return Promise.reject(err);
  },
);
